import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import objectsCategoriesService from "services/objectsCategories.service"
import type { TSimpleCategory, TDualCategory, TCompositeCategory, TColumnBasedCategory } from "types/configuration.type"

export const useObjectsCategories = (filters: { filterAtomic?: boolean } = {}) => {
	return useQuery({
		queryKey: ["objectsCategories", filters],
		queryFn: () => objectsCategoriesService.getObjectsCategories(filters),
		select: ({ data }) => data,
	})
}

export const useIncludeInProcessing = () => {
	const queryClient = useQueryClient()

	return useMutation({
		mutationKey: ["includeInProcessing"],
		mutationFn: ({ includedInProcessing, categoryId }: { categoryId: number; includedInProcessing: boolean }) =>
			objectsCategoriesService.includeInProcessing(includedInProcessing, categoryId),
		onSuccess: async () => {
			// Invalidate and refetch in the background after mutation succeeds
			await queryClient.invalidateQueries({ queryKey: ["objectsCategories"] })
		},
	})
}

export const useCreateSimpleCategory = () => {
	return useMutation({
		mutationKey: ["createSimpleCategory"],
		mutationFn: ({ name, label, superCategory, modelNumbers }: TSimpleCategory) =>
			objectsCategoriesService.createSimpleCategory(name, label, superCategory, modelNumbers),
	})
}

export const useCreateDualCategory = () => {
	return useMutation({
		mutationKey: ["createDualCategory"],
		mutationFn: ({
			name,
			label,
			topLevelCategory,
			superCategory,
			leftModelNumbers,
			rightModelNumbers,
			wholeModelNumbers,
		}: TDualCategory) =>
			objectsCategoriesService.createDualCategory(
				name,
				label,
				topLevelCategory,
				superCategory,
				leftModelNumbers,
				rightModelNumbers,
				wholeModelNumbers,
			),
	})
}

export const useCreateCompositeCategory = () => {
	return useMutation({
		mutationKey: ["createCompositeCategory"],
		mutationFn: ({ name, label, superCategory, modelNumbers, variants }: TCompositeCategory) =>
			objectsCategoriesService.createCompositeCategory(name, label, superCategory, modelNumbers, variants),
	})
}

export const useCreateColumnBasedCategory = () => {
	return useMutation({
		mutationKey: ["createColumnBasedCategory"],
		mutationFn: ({ name, label, superCategory, columns, modelNumbers }: TColumnBasedCategory) => {
			const minColumns = columns[0]
			const maxColumns = columns[1]
			return objectsCategoriesService.createColumnBasedCategory(
				name,
				label,
				superCategory,
				minColumns,
				maxColumns,
				modelNumbers,
			)
		},
	})
}

export const useUpdateModelNumbers = () => {
	const queryClient = useQueryClient()

	return useMutation({
		mutationKey: ["updateModelNumbers"],
		mutationFn: ({ categoryId, modelNumberIds }: { categoryId: number; modelNumberIds: number[] }) =>
			objectsCategoriesService.updateModelNumbers(categoryId, modelNumberIds),
		onSuccess: async () => {
			await Promise.all([
				queryClient.invalidateQueries({ queryKey: ["objectsCategories"] }),
				queryClient.invalidateQueries({ queryKey: ["getModelNumbers"] }),
			])
		},
	})
}

export const useDeleteCategory = () => {
	return useMutation({
		mutationKey: ["deleteCategory"],
		mutationFn: (id: number | undefined) => objectsCategoriesService.deleteCategory(id),
	})
}
